import apiClient from "./apiService";

export type CalendarBucket = "due" | "overdue" | "upcoming" | "completed";

export interface CalendarDayCount {
  date: string;                                  // YYYY-MM-DD
  // Due: items whose due_date == this day. Always present.
  due: number;
  by_source_due: Record<string, number>;

  // Backward-compat aliases (older callers used total/by_source = due).
  total: number;
  by_source: Record<string, number>;

  // Completed-on-day: only on past + today cells.
  completed?: number;
  by_source_completed?: Record<string, number>;

  // Upcoming (next 7 days from this cell): only on today + future cells.
  upcoming?: number;

  // Overdue: ONLY populated on the today cell. Past/future cells have no overdue.
  overdue?: number;
  by_source_overdue?: Record<string, number>;
}

export interface CalendarDueCountsResponse {
  success: boolean;
  data: CalendarDayCount[];
  today: string;                                 // YYYY-MM-DD — backend "now"
  overdue_today: number;
  by_source_overdue_today: Record<string, number>;
}

export interface CalendarDueItem {
  source: string;
  source_label: string;
  source_id: number;
  due_date: string | null;
  start_date: string | null;
  completed_date: string | null;
  is_done: boolean;
  title: string;
  subtitle: string | null;
  role: string;
  route_path: string;
}

export interface CalendarSourceMeta {
  code: string;
  label: string;
  route_path_template: string;
}

const fmt = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export const fetchDueCounts = async (
  from: Date,
  to: Date,
): Promise<CalendarDueCountsResponse> => {
  const res = await apiClient.get<CalendarDueCountsResponse>(
    "/api/v1/cal/due-counts",
    { params: { from: fmt(from), to: fmt(to) } },
  );
  return (
    res.data ?? {
      success: false,
      data: [],
      today: fmt(new Date()),
      overdue_today: 0,
      by_source_overdue_today: {},
    }
  );
};

export const fetchDueItems = async (
  on: Date,
  bucket: CalendarBucket = "due",
  source?: string,
): Promise<CalendarDueItem[]> => {
  const res = await apiClient.get<{ success: boolean; data: CalendarDueItem[] }>(
    "/api/v1/cal/due-items",
    {
      params: {
        date: fmt(on),
        bucket,
        ...(source ? { source } : {}),
      },
    },
  );
  return res.data?.data ?? [];
};

export const fetchCalendarSources = async (): Promise<CalendarSourceMeta[]> => {
  const res = await apiClient.get<{ success: boolean; data: CalendarSourceMeta[] }>(
    "/api/v1/cal/sources",
  );
  return res.data?.data ?? [];
};

export { fmt as fmtDate };
